import { Product, ProductDb } from "./product";
import { ProductStockQuantityField } from "../data/data-types";

export type BundleComponent = {
  itemId: string;
  variantId: number;
  quantity: number;
};

export type BundleComposition = {
  isBundle: boolean;
  components: BundleComponent[];
};

type ItemMetadata = {
  bundle?: BundleComposition;
};

const parseMetadata = (raw: string | null | undefined): ItemMetadata => {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as ItemMetadata;
  } catch {
    return {};
  }
};

export const getBundleComposition = (
  product: Product | null | undefined,
): BundleComposition | null => {
  if (!product?.metadata) return null;
  const meta = parseMetadata(product.metadata);
  return meta.bundle ?? null;
};

export const isBundle = (product: Product | null | undefined): boolean => {
  const c = getBundleComposition(product);
  return !!c?.isBundle && (c?.components?.length ?? 0) > 0;
};

export const setBundleComposition = (
  product: Product,
  composition: BundleComposition | null,
): Product => {
  const existing = parseMetadata(product.metadata);
  const next: ItemMetadata = { ...existing };
  if (composition) {
    next.bundle = composition;
  } else {
    delete next.bundle;
  }
  const hasAny = Object.keys(next).length > 0;
  return {
    ...product,
    metadata: hasAny ? JSON.stringify(next) : undefined,
  };
};

export const emptyBundleComposition = (): BundleComposition => ({
  isBundle: true,
  components: [],
});

/**
 * Full bundle validation used by the Bundle tab (inline alert), the Save button
 * (disable), and the Bundle tab label (warning icon) — single source of truth.
 * Returns an error message, or null when the bundle is valid (or not a bundle).
 */
export const validateBundle = (
  product: Product,
  productDb: ProductDb,
): string | null => {
  const composition = getBundleComposition(product);
  if (!composition?.isBundle) return null;
  if (composition.components.length === 0) {
    return "Add at least one component to make this a bundle.";
  }
  const incomplete = composition.components.findIndex((c) => !c.itemId);
  if (incomplete >= 0) {
    return `Slot ${incomplete + 1} has no product selected.`;
  }
  const dupes = new Set<string>();
  for (const c of composition.components) {
    const key = `${c.itemId}|${c.variantId}`;
    if (dupes.has(key)) {
      return "Two slots reference the same product + variant. Combine them into one slot with higher quantity.";
    }
    dupes.add(key);
  }
  return validateNoRecursion(product.id, composition, productDb);
};

/**
 * Returns null if recursion-safe; otherwise an error message naming the offending component.
 * Phase 1: bundle slots cannot reference other bundles (1-level check; the picker also filters).
 */
export const validateNoRecursion = (
  bundleId: string,
  composition: BundleComposition,
  productDb: ProductDb,
): string | null => {
  for (const slot of composition.components) {
    if (slot.itemId === bundleId) {
      return "Bundle cannot contain itself.";
    }
    const ref = productDb.productMap.get(slot.itemId);
    if (ref && isBundle(ref.product)) {
      return `Component '${ref.product.code || ref.product.name}' is itself a bundle. Bundles cannot contain other bundles.`;
    }
  }
  return null;
};

export type StockStatus = "in" | "out" | "unknown";

export interface SlotStockInfo {
  /** Numeric stock count when known, null when untracked or N/A. */
  quantity: number | null;
  status: StockStatus;
}

/**
 * Computes stock for a bundle slot. For variant-pinned slots we look at that
 * variant's quantity; for whole-product slots we read the product's Stock Quantity
 * field (a custom field) and fall back to the boolean inStock flag if absent.
 */
export const getSlotStock = (
  slot: BundleComponent,
  productDb: ProductDb,
): SlotStockInfo => {
  const ref = productDb.productMap.get(slot.itemId);
  if (!ref) return { quantity: null, status: "out" };

  if (slot.variantId > 0) {
    const v = ref.product.variants?.find((x) => x.id === slot.variantId);
    if (!v) return { quantity: null, status: "out" };
    if (v.quantity === undefined || v.quantity === null) {
      return { quantity: null, status: "unknown" };
    }
    return { quantity: v.quantity, status: v.quantity > 0 ? "in" : "out" };
  }

  // Whole-product slot. Use ProductRef.fieldMap which is keyed by field NAME —
  // product.fields items themselves only carry fieldId/value from the wire,
  // not name (see getProductFields in product.ts), so a name-based find on the
  // array would always miss.
  const qtyField = ref.fieldMap.get(ProductStockQuantityField);
  const parsed = qtyField?.value ? parseInt(qtyField.value, 10) : NaN;
  if (!Number.isNaN(parsed)) {
    return { quantity: parsed, status: parsed > 0 ? "in" : "out" };
  }

  // No explicit quantity — derive from boolean inStock (false now propagates from
  // the server after the ProductModel.From fix).
  if (ref.product.inStock === true) return { quantity: null, status: "in" };
  if (ref.product.inStock === false) return { quantity: null, status: "out" };
  return { quantity: null, status: "unknown" };
};

/**
 * Reads the live Stock Quantity field on the bundle product being edited.
 * Critical to use this rather than product.inStock because inStock is only
 * recomputed server-side on save — pending edits to the Stock Quantity field
 * are reflected in product.fields immediately (by fieldId), not in inStock.
 *
 * Looks up the fieldId via the bundle's category (where field NAMES are
 * defined) and then finds that fieldId in the live product.fields. This is
 * the same lookup pattern editable cells use; raw product.fields items don't
 * carry their name from the API.
 *
 * Returns null when the category has no Stock Quantity field, or when the
 * field exists but isn't a number.
 */
export const readStockQuantity = (
  product: Product,
  productDb: ProductDb,
): number | null => {
  const category = productDb.categoryMap.get(product.categoryId);
  const stockFieldDef = category?.group.fields.find(
    (f) => f.name === ProductStockQuantityField,
  );
  if (!stockFieldDef?.fieldId) return null;
  const pf = product.fields?.find((f) => f.fieldId === stockFieldDef.fieldId);
  if (!pf?.value) return null;
  const n = parseInt(pf.value, 10);
  return Number.isNaN(n) ? null : n;
};

/**
 * Max number of bundles the components could supply, based on the lowest
 * component-stock-per-slot-quantity ratio. Returns null if any component is
 * untracked (can't claim a number).
 */
export const computeComponentMax = (
  composition: BundleComposition,
  productDb: ProductDb,
): number | null => {
  let min: number | null = null;
  for (const slot of composition.components) {
    const stock = getSlotStock(slot, productDb);
    if (stock.status === "unknown") return null;
    const q = stock.quantity;
    if (q === null) continue; // status known (in/out) but no count — skip
    const perBundle = Math.max(1, slot.quantity);
    const slotMax = Math.floor(q / perBundle);
    if (min === null || slotMax < min) min = slotMax;
  }
  return min;
};
